import React, { useState } from "react";
import { Form, InputGroup, Button, Modal } from "react-bootstrap";
import { FaKey, FaEdit, FaEye } from "react-icons/fa";

function SecretInput({ value, onUpdateSecret, onViewSecret, size, label, isAdmin, disabled, canViewSecrets, canEditSecrets }) {
  // If canViewSecrets is not provided, fall back to isAdmin for backwards compatibility
  const allowViewSecrets = canViewSecrets !== undefined ? canViewSecrets : isAdmin;
  // If canEditSecrets is not provided, allow editing (default behavior)
  const allowEditSecrets = canEditSecrets !== undefined ? canEditSecrets : true;

  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [newSecret, setNewSecret] = useState("");
  const [viewedSecret, setViewedSecret] = useState("");
  const [loadingSecret, setLoadingSecret] = useState(false);
  const hasValue = value && value.trim() !== "";

  const handleUpdateClick = () => {
    setShowModal(true);
    setNewSecret("");
  };

  const handleViewClick = async () => {
    setShowViewModal(true);
    setLoadingSecret(true);
    try {
      const secret = await onViewSecret();
      setViewedSecret(secret);
    } catch (error) {
      setViewedSecret("Error loading secret");
    } finally {
      setLoadingSecret(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setNewSecret("");
  };

  const handleViewClose = () => {
    setShowViewModal(false);
    setViewedSecret("");
  };

  const handleSave = () => {
    if (newSecret.trim()) {
      onUpdateSecret(newSecret);
      handleClose();
    }
  };

  return (
    <>
      <InputGroup size={size}>
        {hasValue ? (
          <>
            <Form.Control
              type="text"
              value="••••••••"
              disabled
              size={size}
              style={{ backgroundColor: '#f8f9fa' }}
            />
            {allowViewSecrets && onViewSecret && !disabled && (
              <Button
                variant="outline-info"
                onClick={handleViewClick}
                size={size}
              >
                <FaEye className="me-1" />
                View Secret
              </Button>
            )}
            {allowEditSecrets && (
              <Button
                variant="outline-secondary"
                onClick={handleUpdateClick}
                size={size}
                disabled={disabled}
              >
                <FaEdit className="me-1" />
                Update Secret
              </Button>
            )}
          </>
        ) : (
          <>
            <Form.Control
              type="text"
              value={disabled ? "Save connector first" : "No secret set"}
              disabled
              size={size}
              style={{ backgroundColor: disabled ? '#e9ecef' : '#fff3cd', color: disabled ? '#6c757d' : '#856404' }}
            />
            {allowEditSecrets && (
              <Button
                variant="outline-warning"
                onClick={handleUpdateClick}
                size={size}
                disabled={disabled}
              >
                <FaKey className="me-1" />
                Add Secret
              </Button>
            )}
          </>
        )}
      </InputGroup>

      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>{hasValue ? 'Update' : 'Add'} {label || 'Secret'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Enter new secret value</Form.Label>
            <Form.Control
              type="password"
              value={newSecret}
              onChange={(e) => setNewSecret(e.target.value)}
              placeholder="Enter secret value"
              autoFocus
              autoComplete="new-password"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!newSecret.trim()}>
            Save Secret
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showViewModal} onHide={handleViewClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>View {label || 'Secret'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Secret Value</Form.Label>
            {loadingSecret ? (
              <Form.Control
                type="text"
                value="Loading..."
                disabled
              />
            ) : (
              <Form.Control
                type="text"
                value={viewedSecret}
                readOnly
                style={{ fontFamily: 'monospace', backgroundColor: '#f8f9fa' }}
              />
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleViewClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default SecretInput;
